import { AssetId } from "@carnegie-learning/common-types";
import { Asset } from "@carnegie-learning/cure-types";
import { FeathersServicePagination, FeathersServicePaginationParameters } from "@carnegie-learning/data-access-core";
import { Action, createReducer, on } from "@ngrx/store";
import * as assetsActions from "../actions/student-assets.action";

export interface StudentAssetsState {
    entities: Map<string, Asset>;
    loading: boolean;
    loaded: boolean;
    selectedAssets: Asset[];
    selectedAsset: Asset | null;
    rawResponse: FeathersServicePagination<Asset> | null;
    paginationParams: FeathersServicePaginationParameters<Asset>;
}

export const initialState: StudentAssetsState = {
    entities: new Map<AssetId, Asset>(),
    loading: false,
    loaded: false,
    selectedAssets: [],
    selectedAsset: null,
    rawResponse: null,
    paginationParams: {
        limit: 10,
        skip: 0,
        filter: {},
        sorting: {"$sort[displayName]": 1},
    },
};

const studentAssetReducer = createReducer(
    initialState,
    on(assetsActions.LoadAssets, (state, { paginationParams }) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    }),
    on(assetsActions.LoadAssetsSuccess, (state, { payload }) => {
        const assets: Asset[] = payload.data;

        const entities = new Map<AssetId, Asset>(state.entities.entries());
        assets.forEach((asset) => {
            entities.set(asset.assetId, asset);
        });

        // then re-bind the entity const with our returned state
        return {
            ...state,
            loading: false,
            loaded: true,
            entities: entities,
            rawResponse: payload,
        };
    }),
    on(
        assetsActions.LoadAssetsFail,
        assetsActions.LoadAssetFail,
        (state, { payload }) => {
            return {
                ...state,
                loading: false,
                loaded: false,
            };
        }
    ),
    on(assetsActions.LoadAsset, (state, { assetId }) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    }),
    on(assetsActions.LoadAssetSuccess, (state, { payload }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            selectedAsset: payload,
        };
    }),
    on(assetsActions.SetSelectedAssets, (state, { payload }) => {
        return {
            ...state,
            selectedAssets: payload,
        };
    }),
    on(assetsActions.SetSelectedAsset, (state, { payload }) => {
        return {
            ...state,
            selectedAsset: payload,
        };
    }),
    on(assetsActions.SetAssetsPaginationParams, (state, { payload }) => {
        // Merge parameter values, don't overwrite like LOAD_RESOURCES does.
        const inputParams = payload;
        const paginationParams: any = { ...state.paginationParams }; // TODO: figure out a better way to do this. Should be AssignmentPaginationParameters, not any, but that offends line 105.
        const filter = {};
        if (inputParams) {
            Object.keys(inputParams).forEach(function (key) {
                paginationParams[key] = inputParams[key];
            });
        }

        return {
            ...state,
            paginationParams: paginationParams,
        };
    })
);

// exporting the reducer so it can be added to the index file
export function reducer(state: StudentAssetsState | undefined, action: Action) {
    return studentAssetReducer(state, action);
}

export const getAssetsEntities = (state: StudentAssetsState) => state.entities;
export const getAssetsLoading = (state: StudentAssetsState) => state.loading;
export const getAssetsLoaded = (state: StudentAssetsState) => state.loaded;
export const getSelectedAssets = (state: StudentAssetsState) =>
    state.selectedAssets;
export const getRawAssetsResponse = (state: StudentAssetsState) =>
    state.rawResponse;
export const getAssetPaginationParams = (state: StudentAssetsState) =>
    state.paginationParams;
export const getSelectedAsset = (state: StudentAssetsState) =>
    state.selectedAsset;
export const getSelectedAssetLoading = (state: StudentAssetsState) =>
    state.loading;
export const getSelectedAssetLoaded = (state: StudentAssetsState) =>
    state.loaded;
